import { useMarketRealtime, useInstrumentRealtime } from '@/lib/realtime';
import type { SnakeSide, SnakeTradeDot } from '@/lib/snake-market';

export interface SnakePriceSubscriberProps {
  /** Internal market id (cuid) of the current series instance. */
  marketId: string;
  /** Plaee external market id — resolves the Centrifugo market channel. */
  externalMarketId: string;
  /**
   * Plaee instrument code (e.g. `BTC`) — resolves the underlying-spot firehose.
   * `null` skips the spot subscription.
   */
  instrument: string | null;
  /** Latest Up/Down contract prices (cents) from a `PriceUpdated`. */
  onPrices: (upCents: number, downCents: number) => void;
  /** Latest underlying spot (dollars) from an `InstrumentTick`. */
  onSpot: (spot: number) => void;
  /** A streamed trade for the chart trade-dot overlay. */
  onTradeDot: (dot: SnakeTradeDot) => void;
}

/**
 * Headless subscriber that overlays live data for one snake series.
 *
 * Renders nothing; wires the existing Centrifugo hooks:
 * - {@link useMarketRealtime} → `PriceUpdated` (Up/Down cents) + `TradeStreamed`
 *   (trade dots, mapped Yes→Up / No→Down).
 * - {@link useInstrumentRealtime} → underlying-asset spot ticks (dollars).
 *
 * One instance per visible series so the card / page / buy-panel can show live
 * spot, live Up%, and streaming trade dots (ARC-84 §4–6). The API seed is the
 * first paint.
 */
export function SnakePriceSubscriber({
  marketId,
  externalMarketId,
  instrument,
  onPrices,
  onSpot,
  onTradeDot,
}: SnakePriceSubscriberProps) {
  useMarketRealtime(marketId, externalMarketId, {
    onPriceUpdated: (e) => onPrices(e.yesPrice, e.noPrice),
    onTradeStreamed: (e) => {
      const side: SnakeSide = e.contractSide === 'Yes' ? 'up' : 'down';
      onTradeDot({
        id: `${e.ts}-${e.outcomeId}-${e.price}`,
        time: Math.floor(new Date(e.ts).getTime() / 1000),
        cents: e.price,
        side,
      });
    },
  });

  useInstrumentRealtime(instrument, (tick) => onSpot(tick.lastPrice));

  return null;
}
